import { TimePicker } from '@mui/x-date-pickers-pro';
import { useEffect, useState } from 'react';
import { Line, LineStop, StopDepartureTime } from '../../@types/lines';
import { Box } from '@mui/material';
import LineStopConfirmModal from './LineStopConfirmModal';
import { usePreconfiguredFetch } from '../../hooks/useProvidePreconfiguredFetch';
import { useProvideSnackBar } from '../../hooks';
import { useRefresh } from './RefreshContext';

interface LineStopTimePickerParams {
    lines: Line[];
    lineStop: LineStop;
    onPreviewTime?: (stopId: number, h: number, m: number) => void;
    onClearPreview?: (stopId: number) => void;
}

const TOTAL_MINUTES_IN_DAY = 1440;
const SIXTY = 60;

export default function LineStopTimePicker({ lines, lineStop, onPreviewTime, onClearPreview }: LineStopTimePickerParams) {
    const initialTime = new Date();
    initialTime.setHours(lineStop.DepartureHour, lineStop.DepartureMinute, 0, 0);

    const [value, setValue] = useState<Date>(initialTime);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState<{ hour: number; minute: number } | null>(null);
    const [inProgress, setInProgress] = useState(false);
    const { fetchResource } = usePreconfiguredFetch();
    const { showResponseError } = useProvideSnackBar();
    const { needRefresh, setNeedRefresh, refreshLines } = useRefresh();

    useEffect(() => {
        const newTime = new Date();
        newTime.setHours(lineStop.DepartureHour, lineStop.DepartureMinute, 0, 0);
        setValue(newTime);
    }, [lineStop]);

    const updateOnlyTheSelectedLineStops = (hour: number, minute: number): StopDepartureTime[] => {
        setNeedRefresh(true);
        return lines
            .flatMap(line => line.Stops)
            .filter(stop => stop.Name === lineStop.Name)
            .map(stop => ({
                ID: stop.ID,
                DepartureHour: hour,
                DepartureMinute: minute,
            }));
    };

    const updateAllLineStops = (hour: number, minute: number): StopDepartureTime[] => {
        const originalTotalInMinutes = lineStop.DepartureHour * SIXTY + lineStop.DepartureMinute;
        const newTotalInMinutes = toMinutes(hour, minute);
        const diffInMinutes = newTotalInMinutes - originalTotalInMinutes;

        return lines.flatMap(line =>
            line.Stops.map(stop => {
                const stopTotalInMinutes = toMinutes(stop.DepartureHour, stop.DepartureMinute);
                let updatedTotalInMinutes = stopTotalInMinutes + diffInMinutes;
                updatedTotalInMinutes = (updatedTotalInMinutes + TOTAL_MINUTES_IN_DAY) % TOTAL_MINUTES_IN_DAY;

                return {
                    ID: stop.ID,
                    DepartureHour: Math.floor(updatedTotalInMinutes / SIXTY),
                    DepartureMinute: updatedTotalInMinutes % SIXTY,
                };
            })
        );
    };

    const isUpdated = (hour: number, minute: number): boolean => {
        return toMinutes(lineStop.DepartureHour, lineStop.DepartureMinute) !== toMinutes(hour, minute);
    };

    const toMinutes = (h: number, m: number) => {
        return h * SIXTY + m;
    };

    const save = async (hour: number, minute: number) => {
        if (!isUpdated(hour, minute)) {
            return;
        }
        if (lineStop.Type === 'site' && lineStop.Position === 0) {
            if (needRefresh) {
                await refreshLines();
            }
            setPendingUpdate({ hour, minute });
            setConfirmOpen(true);
            return;
        }

        const stopsToUpdate = updateOnlyTheSelectedLineStops(hour, minute);
        await performUpdate(stopsToUpdate);
    };

    const performUpdate = async (stopDepartureTimeUpdate: StopDepartureTime[]) => {
        setInProgress(true);

        const response = await fetchResource('/api/linestop/departure_time', {
            method: 'PUT',
            body: JSON.stringify({ Stops: stopDepartureTimeUpdate }),
        });
        if (!response.ok) {
            await showResponseError(response);
            await refreshLines();
            setNeedRefresh(false);
            setInProgress(false);
            return;
        }
        setInProgress(false);
    };

    return (
        <>
            <Box
                onKeyDown={e => e.stopPropagation()}
                onBlur={() => {
                    if (!Number.isNaN(value.getHours())) {
                        save(value.getHours(), value.getMinutes());
                    }
                }}>
                <TimePicker
                    disabled={inProgress}
                    ampm={false}
                    openTo='hours'
                    views={['hours', 'minutes']}
                    value={value}
                    onChange={newValue => {
                        if (newValue) {
                            setValue(newValue);
                            const hh = newValue.getHours();
                            const mm = newValue.getMinutes();
                            onPreviewTime?.(lineStop.ID, hh, mm);
                        }
                    }}
                    disableOpenPicker={true}
                    slotProps={{
                        textField: {
                            variant: 'standard',
                            fullWidth: true,
                            sx: {
                                '.MuiButtonBase-root': {
                                    padding: '0',
                                },
                            },
                        },
                    }}
                />
            </Box>

            <LineStopConfirmModal
                open={confirmOpen}
                onConfirm={async () => {
                    if (!pendingUpdate) return;
                    const stops = updateAllLineStops(pendingUpdate.hour, pendingUpdate.minute);
                    await performUpdate(stops);
                    setPendingUpdate(null);
                    setConfirmOpen(false);
                    await refreshLines();
                    setNeedRefresh(false);
                }}
                onCancel={async () => {
                    if (!pendingUpdate) return;
                    const stops = updateOnlyTheSelectedLineStops(pendingUpdate.hour, pendingUpdate.minute);
                    await performUpdate(stops);
                    setPendingUpdate(null);
                    setConfirmOpen(false);
                }}
            />
        </>
    );
}
